import React, { useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { FormikProps } from 'formik';

import { ConfirmModal } from '~/components/ConfirmModal';
import { MainCard, Text } from '~/components/core';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimFieldsFormsResponse } from '~/server_shared/openapi/data-contracts';
import { getLobDescription } from '~/Utils/lobUtils';

import { useSysconfig } from '../../../SystemConfigurationScreen';
import { getFormsUrl } from '../apiService';

import ConfigurationPane from './ConfigurationPane';
import ConfigurationsRail from './ConfigurationsRail';
import type { HeldClaimBar } from './heldEdits';
import type { ConfigurationScope } from './scopes';
import { buildConfigurationScopes, DEFAULT_SCOPE_KEY, isInCallerScope } from './scopes';
import { CONFIGURATIONS_TEXTS as TEXTS } from './texts';

const ClaimBarConfigurationsScreen = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { user, userOrganization } = useCms();
  const isOpenable = (scope: ConfigurationScope) => isInCallerScope(scope, user, userOrganization);
  const [selectedKey, setSelectedKey] = useState(DEFAULT_SCOPE_KEY);
  // The pane holds a scope's changes until Save, and switching scope remounts it, which would drop them.
  const paneFormRef = useRef<FormikProps<HeldClaimBar>>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const [previousPaneHeight, setPreviousPaneHeight] = useState<number>();

  // The next pane shows a skeleton until its configuration loads; at the height the last one had, the page neither
  // shrinks nor loses its scroll position.
  const showScope = (key: string) => {
    setPreviousPaneHeight(paneRef.current?.offsetHeight);
    setSelectedKey(key);
  };

  const selectScope = (key: string) => {
    if (key === selectedKey) {
      return;
    }
    if (paneFormRef.current?.dirty) {
      setPendingKey(key);
      return;
    }
    showScope(key);
  };

  const { data, isError, reloadData } = useDataFetcher<ClaimFieldsFormsResponse>(getFormsUrl(organization.id));

  const { defaultScope, groups } = useMemo(
    () =>
      buildConfigurationScopes(
        organization,
        data?.forms ?? [],
        (lob) => getLobDescription(lob, lobConfigurationsDict) ?? lob
      ),
    [organization, data, lobConfigurationsDict]
  );
  const scopes = [
    defaultScope,
    ...groups.flatMap((group) => [...(group.lobOnly ? [group.lobOnly] : []), ...group.subOrganizations]),
  ];
  const selected = scopes.find((scope) => scope.key === selectedKey) ?? defaultScope;
  const selectedGroup = groups.find((group) => group.lob === selected.lob);
  const selectedCrumbs =
    selected.lob === null
      ? [TEXTS.defaultLabel]
      : selected.subOrganizationId === null
        ? [selected.label]
        : [selectedGroup?.label ?? selected.lob, selected.label];

  if (isError) {
    return <SkeletonTable rowsCount={12} columnsCount={1} isError />;
  }

  return (
    <MainCard type="elevated" title={TEXTS.title}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4 mb-8">
        {organization.sub_organizations_enabled ? TEXTS.description : TEXTS.descriptionWithoutSubOrganizations}
      </Text>
      {data ? (
        <PanelGroup direction="horizontal" id="claim-bar-configurations">
          <Panel
            id="claim-bar-configurations-rail"
            defaultSize={25}
            minSize={18}
            maxSize={40}
            style={{ overflow: 'auto' }}
          >
            <ConfigurationsRail
              defaultScope={defaultScope}
              groups={groups}
              hasSubOrganizations={!!organization.sub_organizations_enabled}
              selectedKey={selected.key}
              onSelect={selectScope}
              isOpenable={isOpenable}
            />
          </Panel>
          <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
          <Panel id="claim-bar-configuration" style={{ overflow: 'auto' }}>
            <div ref={paneRef}>
              <ConfigurationPane
                key={selected.key}
                organizationId={organization.id}
                scope={selected}
                crumbs={selectedCrumbs}
                defaultForm={defaultScope.form}
                reloadForms={reloadData}
                formRef={paneFormRef}
                loadingMinHeight={previousPaneHeight}
                hasSubOrganizations={!!organization.sub_organizations_enabled}
              />
            </div>
          </Panel>
        </PanelGroup>
      ) : (
        <SkeletonTable rowsCount={12} columnsCount={1} />
      )}
      <ConfirmModal
        isOpen={pendingKey !== null}
        title={TEXTS.discardTitle}
        contentText={TEXTS.discardText}
        primaryButtonName={TEXTS.discardConfirm}
        onClose={() => setPendingKey(null)}
        onPrimaryBtnClick={() => {
          showScope(pendingKey ?? selectedKey);
          setPendingKey(null);
        }}
      />
    </MainCard>
  );
};

export default ClaimBarConfigurationsScreen;
